import { useCallback, useEffect, useRef, useState } from "react";
import { api, HttpError } from "@/lib/api";
import type { LogEntry, LogListResponse } from "@/lib/types";
import { type MonitorFilters, toQueryParams } from "@/components/monitor/types";

const POLL_INTERVAL_MS = 10_000;
const MAX_ITEMS = 500;
const PAGE_SIZE = 100;

interface StreamState {
  items: LogEntry[];
  total: number;
  loading: boolean;
  refreshing: boolean;
  paused: boolean;
  error: HttpError | null;
}

/** 실시간 로그 스트림 훅.
 *
 * - 필터 변경 시 전체 리로드
 * - 이후 10초 폴링 (since_id 사용, 신규만 prepend)
 * - 탭 비활성화 시 자동 일시정지 (Page Visibility API)
 * - 클라이언트 측 최대 500건 캐시 (오래된 행 제거)
 */
export function useLogStream(filters: MonitorFilters) {
  const [state, setState] = useState<StreamState>({
    items: [],
    total: 0,
    loading: true,
    refreshing: false,
    paused: false,
    error: null,
  });
  const sinceIdRef = useRef<number | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // ── 전체 리로드 (필터 변경 또는 수동 새로고침) ─────────────────────────────
  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await api.get<LogListResponse>("/logs", {
        ...toQueryParams(filtersRef.current),
        limit: PAGE_SIZE,
      });
      sinceIdRef.current = res.next_since_id;
      setState((s) => ({
        ...s,
        items: res.items,
        total: res.total,
        loading: false,
        error: null,
      }));
    } catch (e) {
      setState((s) => ({
        ...s,
        loading: false,
        error: e instanceof HttpError ? e : new HttpError(0, String(e)),
      }));
    }
  }, []);

  // ── 증분 폴링 ────────────────────────────────────────────────────────────
  const pollOnce = useCallback(async () => {
    if (sinceIdRef.current === null) return;
    setState((s) => ({ ...s, refreshing: true }));
    try {
      const res = await api.get<LogListResponse>("/logs", {
        ...toQueryParams(filtersRef.current),
        since_id: sinceIdRef.current,
        sort: "time_desc",
        limit: PAGE_SIZE,
      });
      if (res.items.length > 0) {
        sinceIdRef.current = res.next_since_id;
        setState((s) => {
          const newIds = new Set(res.items.map((i) => i.id));
          const merged = [
            ...res.items,
            ...s.items.filter((i) => !newIds.has(i.id)),
          ].slice(0, MAX_ITEMS);
          return { ...s, items: merged, refreshing: false };
        });
      } else {
        setState((s) => ({ ...s, refreshing: false }));
      }
    } catch {
      // 폴링 에러는 조용히 — 다음 주기에 재시도
      setState((s) => ({ ...s, refreshing: false }));
    }
  }, []);

  // 필터 변경 → reload
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.from,
    filters.to,
    filters.user_id,
    filters.user_name,
    filters.answered,
    filters.saved,
    filters.feedback,
    filters.slow_threshold_ms,
    filters.q,
    filters.sort,
    filters.include_deleted,
  ]);

  // 폴링 + Page Visibility
  useEffect(() => {
    let timer: number | null = null;

    const start = () => {
      if (timer !== null) return;
      timer = window.setInterval(() => {
        if (!document.hidden) pollOnce();
      }, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        setState((s) => ({ ...s, paused: true }));
      } else {
        setState((s) => ({ ...s, paused: false }));
        pollOnce(); // 즉시 1회
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [pollOnce]);

  return { ...state, reload, pollOnce };
}
